<template>
  <div>
    <div class="section-intro">
      <h2><svg class="icon" aria-hidden="true"><use href="#i-card"/></svg> {{ $t('intake.portal.packIntro') }}</h2>
      <p>{{ $t('intake.portal.packIntroBody') }}</p>
    </div>

    <p v-if="needsLogin" class="empty" role="status">
      {{ $t('intake.portal.needsLogin') }}
      <router-link class="btn btn-ghost" :to="{name: 'login', params: {lang}}">{{ $t('intake.portal.needsLoginCta') }}</router-link>
    </p>

    <div v-else-if="loading" class="empty"><p>{{ $t('intake.portal.loading') }}</p></div>

    <template v-else>
      <article class="therapist-pack-card">
        <span class="avatar avatar-lg" aria-hidden="true">{{ therapistAvatar }}</span>
        <div>
          <p class="eyebrow">{{ $t('intake.portal.packTherapistEyebrow') }}</p>
          <h2>{{ therapistName }}</h2>
          <p v-if="rateLabel">{{ $t('intake.portal.packRate') }} · <b>{{ rateLabel }}</b></p>
          <p v-else>{{ $t(doctor ? 'intake.portal.packRateUnavailable' : 'intake.portal.packRateMissing') }}</p>
        </div>
      </article>

      <p v-if="payError" class="empty" role="alert">{{ payError }}</p>

      <form v-if="options.length" class="package-form" @submit.prevent="pay">
        <fieldset class="package-options" role="radiogroup" :aria-label="$t('intake.portal.packSizeAria')">
          <legend class="visually-hidden">{{ $t('intake.portal.packSizeLegend') }}</legend>
          <label
              v-for="opt in options"
              :key="opt.id"
              :class="['package-option', { 'is-selected': String(packId) === String(opt.id) }]"
          >
            <input v-model="packId" type="radio" name="pack" :value="String(opt.id)">
            <span class="package-option-body">
              <span class="package-count"><b>{{ opt.countLabel }}</b><small>{{ $t('intake.portal.packSessions') }}</small></span>
              <span class="package-copy">
                <strong>{{ opt.title }}</strong>
                <span>{{ opt.copy }}</span>
              </span>
              <span class="package-price">
                <b>{{ opt.priceLabel }}</b>
                <em v-if="opt.wasLabel">{{ opt.wasLabel }}</em>
              </span>
            </span>
          </label>
        </fieldset>
        <p class="package-note">{{ packNote }}</p>
        <div class="actions package-actions">
          <button class="btn btn-primary" type="submit" :disabled="paying || !packId">{{ paying ? $t('intake.portal.loading') : $t('intake.portal.packPayCta') }}</button>
          <router-link class="btn btn-secondary" :to="r('portalPayments')">{{ $t('intake.portal.back') }}</router-link>
        </div>
      </form>

      <div v-else class="empty">
        <h3>{{ $t('intake.portal.packEmptyTitle') }}</h3>
        <p>{{ $t('intake.portal.packEmptyBody') }}</p>
      </div>
    </template>
  </div>
</template>
<script>
import {
  addPackageToCart,
  doctorDisplayName,
  doctorInitials,
  fetchPortalPackageCatalog,
  fetchProfile,
  formatMoneyAmount,
  getCachedProfile,
  hasAuthToken,
  packageSessionCount,
  resolveDoctor,
  toFaDigits,
} from '@/intake/portalApi'

export default {
  name: 'PortalPackageStep',
  data() {
    return {
      loading: true,
      paying: false,
      needsLogin: false,
      payError: '',
      profile: getCachedProfile(),
      packages: [],
      catalogTherapist: null,
      sessionRate: null,
      packId: '',
    }
  },
  computed: {
    lang() {
      return this.$route.params.lang || 'fa'
    },
    doctor() {
      return this.catalogTherapist || resolveDoctor(this.profile)
    },
    therapistName() {
      return doctorDisplayName(this.doctor, this.lang) || this.$t('intake.portal.therapistNameEmpty')
    },
    therapistAvatar() {
      return doctorInitials(this.doctor, this.lang) || '—'
    },
    rateLabel() {
      return this.sessionRate && Number(this.sessionRate.amount) > 0
        ? formatMoneyAmount(this.sessionRate.amount, this.sessionRate.currency, this.lang) : ''
    },
    packNote() {
      if (this.doctor) {
        return this.$t('intake.portal.packNoteLive', {name: this.therapistName})
      }
      return this.$t('intake.portal.packNoteGeneric')
    },
    options() {
      const sorted = this.packages.slice().sort((a, b) => packageSessionCount(a) - packageSessionCount(b))
      return sorted.map((pkg) => {
        const count = packageSessionCount(pkg)
        const displayCount = this.lang === 'fa' ? toFaDigits(count) : String(count)
        const price = formatMoneyAmount(pkg.price, pkg.currency || pkg.currency_code, this.lang)
        const was = pkg.old_price || pkg.was_price || pkg.original_price
        return {
          id: pkg.id,
          countLabel: this.lang === 'fa' ? toFaDigits(count || '—') : String(count || '—'),
          title: this.$t('intake.portal.packCountTitle', {n: displayCount}),
          copy: pkg.description || pkg.en_description || this.$t('intake.portal.packFallbackCopy'),
          priceLabel: price,
          wasLabel: was != null ? formatMoneyAmount(was, pkg.currency || pkg.currency_code, this.lang) : '',
        }
      })
    },
  },
  mounted() {
    this.bootstrap()
  },
  methods: {
    r(name) {
      return {name, params: {lang: this.lang}}
    },
    bootstrap() {
      this.loading = true
      this.needsLogin = !hasAuthToken()
      if (this.needsLogin) {
        this.loading = false
        return
      }
      fetchProfile()
          .catch(() => getCachedProfile())
          .then((profile) => {
            this.profile = profile
            return fetchPortalPackageCatalog()
          })
          .then((catalog) => {
            this.packages = catalog.packages || []
            this.sessionRate = catalog.session_rate
            this.catalogTherapist = catalog.therapist
            const first = this.packages.slice().sort((a, b) => packageSessionCount(a) - packageSessionCount(b))[0]
            this.packId = first ? String(first.id) : ''
          })
          .catch(() => {
            this.packages = []
            this.sessionRate = null
            this.packId = ''
          })
          .finally(() => {
            this.loading = false
          })
    },
    pay() {
      if (!this.packId || this.paying || !this.packages.some((pkg) => String(pkg.id) === String(this.packId))) return
      this.paying = true
      this.payError = ''
      const selected = this.packages.find((pkg) => String(pkg.id) === String(this.packId))
      addPackageToCart(this.packId, selected.price_record_id)
          .then(() => {
            this.$router.push({name: 'basket', params: {lang: this.lang}})
          })
          .catch((error) => {
            const code = error && error.response && error.response.data && error.response.data.data && error.response.data.data.reason_code
            this.payError = this.$t(code === 'CARE_PACKAGE_PRICE_CHANGED' ? 'intake.portal.packPriceChanged' : 'intake.portal.packPayError')
          })
          .finally(() => {
            this.paying = false
          })
    },
  },
}
</script>
